import React, { useState, useEffect, useRef } from 'react';
import { Calendar, X } from 'lucide-react';
import clsx from 'clsx';
import Button from './Button';
import Select, { SelectOption } from './Select';
import ModalLoadingOverlay from './ModalLoadingOverlay';
import Message from './Message';
import { DateInput, Textarea } from '@components/common';
import {
  Contract,
  MeetingDateSuggestion,
  ScheduleItemContract,
  MEETING_CANCELLATION_REASON,
  MEETING_RESCHEDULE_REASON,
  CANCELLATION_REASON_LABELS,
  RESCHEDULE_REASON_LABELS
} from '@app-types';
import { schedulerService } from '@services';
import {
  utcToLocalForInput,
  localInputToUTC,
  formatDate,
  formatSuggestionDay,
  getNextAvailableHour,
  isDateWithinConstraints,
  getDateConstraints
} from '@utils/dateUtils';
import styles from './ScheduledDateModal.module.css';

interface ScheduledDateModalProps {
  contract: Contract | ScheduleItemContract;
  scheduleItemId?: number | null; // For scheduler modal - use schedule item ID instead of contract ID
  meetingId?: number; // For assessor modal - use meeting ID
  currentDate?: string | null;
  isLoading?: boolean;
  onClose: () => void;
  onDateSelected: (date: string, reason?: string, notes?: string) => Promise<void>;
  dataTestId?: string;
}

const ScheduledDateModal: React.FC<ScheduledDateModalProps> = ({
  contract,
  scheduleItemId,
  meetingId,
  currentDate,
  isLoading = false,
  onClose,
  onDateSelected,
  dataTestId = 'scheduled-date'
}) => {
  const [selectedDate, setSelectedDate] = useState<string>(currentDate || '');
  const [rescheduleReason, setRescheduleReason] = useState<string>('');
  const [cancellationReason, setCancellationReason] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [isCanceling, setIsCanceling] = useState(false); // Track if user is in cancellation mode
  const [suggestedDates, setSuggestedDates] = useState<MeetingDateSuggestion[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null);
  const [usingFallbackDates, setUsingFallbackDates] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const contentRef = useRef<HTMLDivElement>(null);

  // Update selectedDate when currentDate changes
  useEffect(() => {
    setSelectedDate(currentDate || '');
  }, [currentDate]);

  // Scroll to bottom when "Other" reason is selected and notes field appears
  useEffect(() => {
    if (
      (rescheduleReason === MEETING_RESCHEDULE_REASON.OTHER ||
        cancellationReason === MEETING_CANCELLATION_REASON.OTHER) &&
      contentRef.current
    ) {
      // Instant scroll to bottom without animation
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [rescheduleReason, cancellationReason]);

  // Get minimum and maximum allowed dates
  const dateConstraints = getDateConstraints(contract.target_date);

  // Determine context: Assessor context = meetingId defined but scheduleItemId not defined
  const isAssessorContext = meetingId !== undefined && scheduleItemId === undefined;
  // Any role rescheduling a booked meeting must give a reason (ADO-1147); the backend rejects it otherwise.
  const isRescheduleMode = Boolean(currentDate);
  // Cancelling stays Assessor-only: the Scheduler page has no cancellation-reason flow (ADO-1147).
  const canCancelMeeting = isAssessorContext && isRescheduleMode;

  // Prepare reason options for dropdowns
  const rescheduleReasonOptions: SelectOption[] = Object.values(MEETING_RESCHEDULE_REASON).map((value) => ({
    value,
    label: RESCHEDULE_REASON_LABELS[value]
  }));

  const cancellationReasonOptions: SelectOption[] = Object.values(MEETING_CANCELLATION_REASON).map((value) => ({
    value,
    label: CANCELLATION_REASON_LABELS[value]
  }));

  // Load suggestions when modal opens
  useEffect(() => {
    const loadSuggestedDates = async () => {
      // Determine context: Assessor (meetingId) vs Scheduler (scheduleItemId) vs Legacy (contract.id)
      const isAssessorContext = meetingId !== undefined;
      const isSchedulerContext = scheduleItemId !== undefined && scheduleItemId !== null;

      setLoadingSuggestions(true);
      setSuggestionsError(null);
      setUsingFallbackDates(false);

      try {
        let suggestions: MeetingDateSuggestion[] = [];

        if (isAssessorContext) {
          // Assessor context - use getMeetingDateSuggestions with group type
          suggestions = await schedulerService.getMeetingDateSuggestions(meetingId!, 'group');
        } else {
          // Scheduler or legacy context - use getMeetingDateSuggestions
          const itemId = isSchedulerContext ? scheduleItemId : contract.id;

          // Skip loading suggestions if no valid ID
          if (!itemId) {
            setSuggestionsError('Unable to load suggestions');
            setUsingFallbackDates(true);
            return;
          }

          // Determine type: if scheduleItemId is provided, use 'group', otherwise 'contract'
          const type = isSchedulerContext ? 'group' : 'contract';
          suggestions = await schedulerService.getMeetingDateSuggestions(itemId, type);
        }

        if (suggestions && suggestions.length > 0) {
          setSuggestedDates(suggestions);
          setUsingFallbackDates(false);
        } else {
          // No error - just no available dates, show empty state
          setSuggestedDates([]);
          setUsingFallbackDates(true);
        }
      } catch (error) {
        console.error('Failed to load meeting date suggestions:', error);
        setSuggestionsError('Calendar service unavailable - please use custom date picker below');
        setSuggestedDates([]);
        setUsingFallbackDates(true);
      } finally {
        setLoadingSuggestions(false);
      }
    };

    loadSuggestedDates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once when modal opens

  // Auto-fill input with next available hour when modal opens
  useEffect(() => {
    if (!currentDate && !selectedDate) {
      setSelectedDate(getNextAvailableHour());
    }
  }, [currentDate, selectedDate]);

  const formatSuggestionLabel = (suggestion: MeetingDateSuggestion): string => {
    return formatDate(suggestion.startDateTime);
  };

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
    setError(null);
  };

  // Validate selected date against constraints
  const isDateValid = (date: string): boolean => {
    return isDateWithinConstraints(date, dateConstraints.minDate, dateConstraints.maxDate);
  };

  // Helper: Validate reschedule inputs
  const validateReschedule = (): string | null => {
    if (!selectedDate) {
      return 'Please select a date';
    }

    if (isRescheduleMode) {
      if (!rescheduleReason) {
        return 'Please select a reason for rescheduling';
      }

      if (rescheduleReason === MEETING_RESCHEDULE_REASON.OTHER && !notes.trim()) {
        return 'Please provide additional details for rescheduling';
      }
    }

    if (!isDateValid(selectedDate)) {
      return 'Selected date is outside the allowed range';
    }

    return null;
  };

  // Helper: Validate cancellation inputs
  const validateCancellation = (): string | null => {
    if (!cancellationReason) {
      return 'Please select a reason for canceling the meeting';
    }

    if (cancellationReason === MEETING_CANCELLATION_REASON.OTHER && !notes.trim()) {
      return 'Please provide additional details for cancellation';
    }

    return null;
  };

  const handleConfirm = async () => {
    setError(null);

    const validationError = validateReschedule();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    try {
      if (isRescheduleMode) {
        await onDateSelected(selectedDate, rescheduleReason, notes || undefined);
      } else {
        await onDateSelected(selectedDate);
      }
      onClose();
    } catch (error) {
      console.error('Failed to schedule meeting:', error);
      setError('Failed to schedule meeting. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleRemoveDate = async () => {
    setError(null);

    const validationError = validateCancellation();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    try {
      await onDateSelected('', cancellationReason, notes || undefined);
      onClose();
    } catch (error) {
      console.error('Failed to cancel meeting:', error);
      setError('Failed to cancel meeting. Please try again.');
      setIsSubmitting(false);
    }
  };

  // Render date suggestions loading/error states
  const renderDateSuggestionsState = () => {
    if (loadingSuggestions) {
      return (
        <div className={styles.scheduledDateModalLoading}>
          <div className={styles.scheduledDateModalLoadingSpinner}></div>
          Loading suggested dates from calendar...
        </div>
      );
    }

    if (suggestionsError) {
      return (
        <div className={styles.scheduledDateModalError}>
          <div className={styles.scheduledDateModalErrorIcon}>⚠️</div>
          <div className={styles.scheduledDateModalErrorContent}>
            <div className={styles.scheduledDateModalErrorTitle}>Calendar Integration Issue</div>
            <div className={styles.scheduledDateModalErrorMessage}>{suggestionsError}</div>
            <div className={styles.scheduledDateModalErrorFallback}>
              Please use the custom date picker below to schedule your meeting.
            </div>
          </div>
        </div>
      );
    }

    if (usingFallbackDates) {
      return (
        <div className={styles.scheduledDateModalInfo}>
          <div className={styles.scheduledDateModalInfoIcon}>ℹ️</div>
          <div className={styles.scheduledDateModalInfoContent}>
            <div className={styles.scheduledDateModalInfoTitle}>No Calendar Availability</div>
            <div className={styles.scheduledDateModalInfoMessage}>
              No suggested dates available from calendar service. Please use the custom date picker below.
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  // Render suggested date options grid
  const renderSuggestedDates = () => {
    if (loadingSuggestions || suggestedDates.length === 0) {
      return null;
    }

    return (
      <div className={styles.scheduledDateModalDatesGrid}>
        {suggestedDates.map((suggestion) => {
          const isLowConfidence = String(suggestion.confidence) === 'low';
          const isSelected = selectedDate === suggestion.startDateTime;
          const isDisabled = isCanceling || isLowConfidence;

          return (
            <button
              type="button"
              key={suggestion.startDateTime}
              className={clsx(
                styles.scheduledDateModalDateOption,
                isSelected && styles.selected,
                isLowConfidence && styles.unavailable,
                isCanceling && styles.disabled
              )}
              onClick={() => !isDisabled && handleDateSelect(suggestion.startDateTime)}
              disabled={isDisabled}
              title={suggestion.suggestionReason || ''}
            >
              <div className={styles.scheduledDateModalDateDay}>{formatSuggestionDay(suggestion.startDateTime)}</div>
              <div className={styles.scheduledDateModalDateFull}>{formatSuggestionLabel(suggestion)}</div>
            </button>
          );
        })}
      </div>
    );
  };

  // Render reschedule reason fields
  const renderRescheduleReason = () => {
    if (!isRescheduleMode || isCanceling) {
      return null;
    }

    return (
      <>
        <div className={styles.reasonField}>
          <label htmlFor="reschedule-reason">
            Reason for Rescheduling <span className="required">*</span>
          </label>
          <Select
            options={rescheduleReasonOptions}
            value={rescheduleReason}
            onChange={(value) => {
              setRescheduleReason(value);
              setError(null);
              if (value !== MEETING_RESCHEDULE_REASON.OTHER) {
                setNotes('');
              }
            }}
            placeholder="Select a reason..."
            disabled={isLoading}
            error={!!error && !rescheduleReason}
          />
        </div>

        {rescheduleReason === MEETING_RESCHEDULE_REASON.OTHER && (
          <div className={styles.reasonField}>
            <Textarea
              id="reschedule-notes"
              label="Additional Details"
              required
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                setError(null);
              }}
              placeholder="Please provide additional details..."
              rows={3}
              disabled={isLoading}
              dataTestId="reschedule-notes"
            />
          </div>
        )}
      </>
    );
  };

  // Render cancellation reason fields
  const renderCancellationReason = () => {
    if (!canCancelMeeting || !isCanceling) {
      return null;
    }

    return (
      <>
        <div className={styles.reasonField}>
          <label htmlFor="cancellation-reason">
            Reason for Cancellation <span className="required">*</span>
          </label>
          <Select
            options={cancellationReasonOptions}
            value={cancellationReason}
            onChange={(value) => {
              setCancellationReason(value);
              setError(null);
              if (value !== MEETING_CANCELLATION_REASON.OTHER) {
                setNotes('');
              }
            }}
            placeholder="Select a reason..."
            disabled={isLoading}
            error={!!error && !cancellationReason}
          />
        </div>

        {cancellationReason === MEETING_CANCELLATION_REASON.OTHER && (
          <div className={styles.reasonField}>
            <Textarea
              id="cancellation-notes"
              label="Additional Details"
              required
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                setError(null);
              }}
              placeholder="Please provide additional details..."
              rows={3}
              disabled={isLoading}
              dataTestId="cancellation-notes"
            />
          </div>
        )}
      </>
    );
  };

  return (
    <div className={styles.scheduledDateModalOverlay} data-test-id={`${dataTestId}-modal`}>
      <div className={styles.scheduledDateModal}>
        {isSubmitting && (
          <ModalLoadingOverlay message={currentDate ? 'Updating meeting...' : 'Scheduling meeting...'} />
        )}

        <div className={styles.scheduledDateModalHeader}>
          <div className={styles.scheduledDateModalTitle} data-test-id={`${dataTestId}-title`}>
            <Calendar size={20} />
            Schedule Meeting Date
          </div>
          <button className={styles.scheduledDateModalClose} onClick={onClose} disabled={isSubmitting}>
            <X size={20} />
          </button>
        </div>

        <div className={styles.scheduledDateModalContent} ref={contentRef}>
          {/* Contract Info */}
          <div className={styles.scheduledDateModalContractInfo}>
            <div className={styles.scheduledDateModalContractDetails}>
              <h4>{contract.contract_number}</h4>
              <p>{contract.name}</p>
            </div>
            <div className={styles.scheduledDateModalCurrentDate}>
              <span className={styles.scheduledDateModalLabel}>Current Date:</span>
              <span className={clsx(styles.scheduledDateModalDate, currentDate ? styles.hasDate : styles.noDate)}>
                {currentDate ? formatDate(currentDate) : 'Not scheduled'}
              </span>
            </div>
            <div className={styles.scheduledDateModalDateConstraints}>
              <small>
                <span className={styles.scheduledDateModalConstraintLabel}>
                  Meeting cannot be scheduled in the past
                </span>
                <br />
                <span className={styles.scheduledDateModalConstraintBullet}>•</span> Minimum:{' '}
                {dateConstraints.minDate.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                })}{' '}
                (Current time)
              </small>
            </div>
          </div>

          {/* Date Selection */}
          <div className={styles.scheduledDateModalDateSelection}>
            <h4>Select Meeting Date</h4>
            {renderDateSuggestionsState()}
            {renderSuggestedDates()}
          </div>

          {/* Custom Date Input */}
          <div className={styles.scheduledDateModalCustomDate}>
            <DateInput
              id="custom-date"
              label="Or select custom date and time:"
              type="datetime-local"
              value={utcToLocalForInput(selectedDate)}
              min={dateConstraints.minDate.toLocaleString('sv-SE').slice(0, 16)}
              max={dateConstraints.maxDate.toLocaleString('sv-SE').slice(0, 16)}
              onClick={() => {
                // Auto-fill with next available hour if empty
                if (!selectedDate && !isCanceling) {
                  setSelectedDate(getNextAvailableHour());
                }
              }}
              onChange={(e) => {
                setError(null);
                if (e.target.value) {
                  // Convert local input to UTC for backend storage
                  const utcISOString = localInputToUTC(e.target.value);
                  setSelectedDate(utcISOString);
                } else {
                  setSelectedDate('');
                }
              }}
              disabled={isCanceling}
              wrapperClassName={styles.scheduledDateModalDateInputWrapper}
            />
          </div>

          {/* Reason Fields */}
          {renderCancellationReason()}
          {renderRescheduleReason()}

          {/* Error Message */}
          {error && <Message variant="error">{error}</Message>}
        </div>

        <div className={styles.scheduledDateModalFooter}>
          <div className={styles.scheduledDateModalActions}>
            {canCancelMeeting && !isCanceling && (
              <Button
                variant="ghost"
                onClick={() => {
                  setIsCanceling(true);
                  setError(null);
                }}
                disabled={isSubmitting}
                className={styles.scheduledDateModalRemoveBtn}
              >
                Cancel Meeting
              </Button>
            )}
            <div className={styles.scheduledDateModalPrimaryActions}>
              {isCanceling ? (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setIsCanceling(false);
                      setCancellationReason('');
                      setNotes('');
                      setError(null);
                    }}
                    disabled={isSubmitting}
                  >
                    Back
                  </Button>
                  <Button variant="danger" onClick={handleRemoveDate} disabled={isSubmitting}>
                    {isSubmitting ? 'Canceling...' : 'Confirm Cancellation'}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleConfirm}
                    disabled={!selectedDate || !isDateValid(selectedDate) || isSubmitting}
                  >
                    {(() => {
                      if (isSubmitting) return 'Scheduling...';
                      return currentDate ? 'Update Date' : 'Schedule Date';
                    })()}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduledDateModal;
